import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { SAMPLE_QUEUE, WGS_QUEUE, LAB_USERS, getLabStats, recieveSample, saveLabResult, rejectSample } from './data/store';
import { buildFHIRDiagnotsicReport, classifyTB, shouldTriggerWGS, buildDHIS2DataValueSet } from './fhir/utils';

// ── DESIGN TOKENS ─────────────────────────────────────────────────────
const T = {
  navy:    '#0B1929', navyMid: '#112240', navyLight: '#1A3A5C',
  blue:    '#1E5F8A', blueLight: '#2980B9', accent: '#00D4FF',
  teal:    '#00897B', tealLight: '#26A69A',
  amber:   '#F39C12', red: '#E74C3C', green: '#27AE60', purple: '#8E44AD',
  text:    '#E8F4FD', textMuted: '#7FB3D3', textDim: '#4A7A9B',
  border:  '#1E3A5F', cardBg: '#112240', elevated: '#1A3A5C',
  white:   '#FFFFFF',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; }
  body { background: ${T.navy}; color: ${T.text}; font-family: 'DM Sans', sans-serif; overflow-x: hidden; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${T.navyMid}; }
  ::-webkit-scrollbar-thumb { background: ${T.blue}; border-radius: 3px; }
  input, select, textarea { font-family: 'DM Sans', sans-serif; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  @keyframes slideIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes glow { 0%,100%{box-shadow:0 0 8px ${T.accent}40} 50%{box-shadow:0 0 20px ${T.accent}80} }
  .slide-in { animation: slideIn 0.3s ease forwards; }
  .pulse { animation: pulse 2s infinite; }
  .glow { animation: glow 3s infinite; }
`;

// ── STATUS CONFIG ─────────────────────────────────────────────────────
const STATUS = {
  RECEIVED_NMRL: { label: 'Awaiting Processing', color: T.blueLight, bg: '#1E5F8A30', dot: T.blueLight },
  PROCESSING:    { label: 'Processing',          color: T.amber,     bg: '#F39C1230', dot: T.amber     },
  COMPLETE:      { label: 'Complete',            color: T.green,     bg: '#27AE6030', dot: T.green     },
  REJECTED:      { label: 'Rejected',            color: T.red,       bg: '#E74C3C30', dot: T.red       },
};

const TB_CLASS = {
  'DS-TB':      { color: T.green,  bg: '#27AE6025', icon: '✓' },
  'HR-TB':      { color: T.amber,  bg: '#F39C1225', icon: '⚠' },
  'RR-TB':      { color: T.red,    bg: '#E74C3C25', icon: '!' },
  'MDR-TB':     { color: T.red,    bg: '#E74C3C25', icon: '!!' },
  'Pre-XDR-TB': { color: '#C0392B',bg: '#C0392B25', icon: '!!!' },
  'XDR-TB':     { color: '#922B21',bg: '#922B2125', icon: '!!!!' },
  'Pending':    { color: T.textMuted, bg: '#4A7A9B25', icon: '?' },
};

// ── STYLE HELPERS ─────────────────────────────────────────────────────
const card = (extra = {}) => ({
  background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 12,
  padding: '20px', ...extra,
});
const badge = (color, bg) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
  color, background: bg, border: `1px solid ${color}40`,
});
const btn = (color, variant = 'filled') => ({
  padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: 'none', transition: 'all 0.2s',
  ...(variant === 'filled'
    ? { background: color, color: T.white }
    : { background: color + '20', color, border: `1px solid ${color}50` }),
});
const field = () => ({
  width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
  background: T.elevated, border: `1px solid ${T.border}`, color: T.text,
  outline: 'none', transition: 'border 0.2s',
});

// ── COMPONENTS ────────────────────────────────────────────────────────
function Dot({ color, pulse }) {
  return (
    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, ...(pulse ? { animation: 'pulse 1.5s infinite' } : {}) }} />
  );
}

function StatCard({ icon, value, label, sub, color, onClick }) {
  return (
    <div onClick={onClick} style={{ ...card({ cursor: onClick ? 'pointer' : 'default', borderTop: `3px solid ${color}`, transition: 'transform 0.2s' }), ':hover': { transform: 'translateY(-2px)' } }}
      onMouseEnter={e => onClick && (e.currentTarget.style.transform = 'translateY(-2px)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.transform = 'translateY(0)')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <span style={{ fontSize: 24 }}>{icon}</span>
        {sub && <span style={{ fontSize: 11, color: T.textMuted, background: T.elevated, padding: '2px 8px', borderRadius: 99 }}>{sub}</span>}
      </div>
      <div style={{ fontSize: 36, fontWeight: 700, color, fontFamily: 'Space Mono', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>{label}</div>
    </div>
  );
}

function SampleRow({ sample, onSelect, selected }) {
  const st  = STATUS[sample.status] || STATUS.RECEIVED_NMRL;
  const cls = sample.lab_result?.tb_classification;
  const clsCfg = cls ? TB_CLASS[cls] : null;
  return (
    <div onClick={() => onSelect(sample)} style={{
      display: 'grid', gridTemplateColumns: '1fr 120px 100px 110px 90px 80px',
      alignItems: 'center', padding: '14px 20px', cursor: 'pointer',
      background: selected ? T.elevated : 'transparent',
      borderLeft: selected ? `3px solid ${T.accent}` : '3px solid transparent',
      borderBottom: `1px solid ${T.border}`, transition: 'all 0.15s', gap: 12,
    }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = T.navyLight + '60'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div>
        <div style={{ fontFamily: 'Space Mono', fontSize: 13, color: T.accent, marginBottom: 2 }}>{sample.sample_code}</div>
        <div style={{ fontSize: 12, color: T.textMuted }}>{sample.clinic.name} · {sample.sample_type}</div>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted }}>{sample.received_date ? new Date(sample.received_date).toLocaleDateString() : '—'}</div>
      <div>
        <span style={badge(st.color, st.bg)}>
          <Dot color={st.dot} pulse={sample.status === 'PROCESSING'} />
          {st.label}
        </span>
      </div>
      <div>
        {clsCfg
          ? <span style={badge(clsCfg.color, clsCfg.bg)}>{clsCfg.icon} {cls}</span>
          : <span style={{ fontSize: 12, color: T.textDim }}>Pending</span>}
      </div>
      <div>
        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: sample.priority === 'urgent' ? T.red + '25' : T.elevated, color: sample.priority === 'urgent' ? T.red : T.textMuted, border: `1px solid ${sample.priority === 'urgent' ? T.red + '50' : T.border}`, fontWeight: 600 }}>
          {sample.priority}
        </span>
      </div>
      <div>
        {sample.lab_result?.wgs_requested
          ? <span style={{ ...badge(T.purple, T.purple + '25'), fontSize: 10 }}>🧬 WGS</span>
          : <span style={{ fontSize: 11, color: T.textDim }}>—</span>}
      </div>
    </div>
  );
}

// ── LAB RESULT FORM ───────────────────────────────────────────────────
function LabResultForm({ sample, onSubmit, onReject, onClose }) {
  const [form, setForm] = useState({
    smear_result: '', smear_grade: '', smear_date: '',
    genexpert_result: '', genexpert_rif_resist: '', genexpert_date: '',
    culture_result: '', culture_date: '',
    dst_isoniazid: '', dst_rifampicin: '', dst_pyrazinamide: '', dst_ethambutol: '',
    dst_moxifloxacin: '', dst_bedaquiline: '', dst_date: '',
    notes: '',
  });
  const [step,          setStep]          = useState(0);
  const [preview,       setPreview]       = useState(null);
  const [rejectReason,  setRejectReason]  = useState('');
  const [showReject,    setShowReject]    = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasGxpert = form.genexpert_result !== '';
  const previewClass = form.genexpert_result || form.dst_rifampicin ? classifyTB(form) : null;
  const wgsPreview   = previewClass ? shouldTriggerWGS({ ...form, tb_classification: previewClass.classification }) : null;

  const Label = ({ children }) => <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.7px' }}>{children}</div>;
  const Select = ({ field: f, options, placeholder = 'Select...' }) => (
    <select value={form[f]} onChange={e => set(f, e.target.value)} style={field()}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.v || o} value={o.v || o}>{o.l || o}</option>)}
    </select>
  );
  const RESIST_OPTS = [{ v: 'Susceptible', l: 'Susceptible (S)' }, { v: 'Resistant', l: 'Resistant (R)' }, { v: 'Intermediate', l: 'Intermediate (I)' }, { v: 'Not tested', l: 'Not tested' }];
  const GXPERT_OPTS = [{ v: 'MTB DETECTED', l: 'MTB Detected' }, { v: 'NOT DETECTED', l: 'Not Detected' }, { v: 'INVALID', l: 'Invalid' }, { v: 'ERROR', l: 'Error' }];
  const RIF_OPTS    = [{ v: 'DETECTED', l: 'Detected (Resistant)' }, { v: 'NOT DETECTED', l: 'Not Detected (Susceptible)' }, { v: 'INDETERMINATE', l: 'Indeterminate' }];

  const steps = ['Microscopy', 'GeneXpert', 'Culture & DST', 'Review & Submit'];

  const handleSubmit = () => {
    const r = onSubmit(sample.id, form);
    setPreview(r);
  };

  if (preview) return (
    <div style={{ ...card(), animation: 'slideIn 0.3s ease' }}>
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
        <h3 style={{ fontSize: 20, marginBottom: 8 }}>Results Saved</h3>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {preview.classification && (
            <span style={badge(TB_CLASS[preview.classification.classification]?.color || T.green, TB_CLASS[preview.classification.classification]?.bg || T.green + '20')}>
              {preview.classification.classification}
            </span>
          )}
          {preview.wgsTrigger?.triggered && <span style={badge(T.purple, T.purple + '25')}>🧬 WGS Triggered</span>}
        </div>
        {preview.wgsTrigger?.triggered && (
          <div style={{ background: T.purple + '15', border: `1px solid ${T.purple}40`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.purple, marginBottom: 6 }}>🧬 WGS Request Auto-Generated</div>
            {preview.wgsTrigger.reasons.map((r, i) => <div key={i} style={{ fontSize: 12, color: T.textMuted, marginBottom: 3 }}>· {r}</div>)}
          </div>
        )}
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>
          Results sent to originating clinic · FHIR R4 DiagnosticReport generated · DHIS2 data value set ready
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button style={btn(T.blueLight)} onClick={onClose}>← Back to Queue</button>
          <button style={btn(T.teal, 'outline')} onClick={() => {
            const report = buildFHIRDiagnosticReport(sample, form, sample.patient);
            const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `FHIR_${sample.sample_code}.json`; a.click();
          }}>⬇ FHIR Report</button>
          <button style={btn(T.amber, 'outline')} onClick={() => {
            const dhis2 = buildDHIS2DataValueSet(sample, { ...form, ...preview.classification }, 'NMRL_HQ');
            const blob = new Blob([JSON.stringify(dhis2, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `DHIS2_${sample.sample_code}.json`; a.click();
          }}>⬇ DHIS2 Export</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ ...card(), animation: 'slideIn 0.3s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: 'Space Mono', fontSize: 16, color: T.accent }}>{sample.sample_code}</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>
            {sample.clinic.name} · {sample.sample_type} · {sample.patient.age}y {sample.patient.sex} · HIV: {sample.patient.hiv_status}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 20 }}>✕</button>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 0 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div onClick={() => i < step && setStep(i)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: i < step ? 'pointer' : 'default' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: i < step ? T.green : i === step ? T.accent : T.elevated, color: i <= step ? T.navy : T.textDim, border: `2px solid ${i === step ? T.accent : i < step ? T.green : T.border}` }}>
                {i < step ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 10, color: i === step ? T.accent : T.textDim, marginTop: 4, whiteSpace: 'nowrap' }}>{s}</div>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? T.green : T.border, margin: '0 4px', marginBottom: 18 }} />}
          </React.Fragment>
        ))}
      </div>

      {/* Live classification preview */}
      {previewClass && previewClass.classification !== 'Pending' && (
        <div style={{ background: TB_CLASS[previewClass.classification]?.bg || T.teal + '15', border: `1px solid ${previewClass.color}40`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 2 }}>Live Classification</div>
            <span style={badge(previewClass.color, TB_CLASS[previewClass.classification]?.bg || '')}>
              {TB_CLASS[previewClass.classification]?.icon} {previewClass.classification}
            </span>
          </div>
          {wgsPreview?.triggered && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...badge(T.purple, T.purple + '20'), animation: 'glow 3s infinite' }}>🧬 WGS will be triggered</span>
            </div>
          )}
        </div>
      )}

      {/* Step 0 — Smear Microscopy */}
      {step === 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div><Label>Smear Result</Label><Select field="smear_result" options={['Positive', 'Negative', 'Scanty', 'Not done']} /></div>
          <div><Label>Smear Grade</Label><Select field="smear_grade" options={['+', '++', '+++', 'Scanty']} placeholder="Select grade..." /></div>
          <div><Label>Date Performed</Label><input type="date" value={form.smear_date} onChange={e => set('smear_date', e.target.value)} style={field()} /></div>
        </div>
      )}

      {/* Step 1 — GeneXpert */}
      {step === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <Label>GeneXpert MTB Result</Label>
            <Select field="genexpert_result" options={GXPERT_OPTS} />
          </div>
          <div>
            <Label>Rifampicin Resistance</Label>
            <Select field="genexpert_rif_resist" options={RIF_OPTS} placeholder={hasGxpert ? 'Select...' : 'Run GeneXpert first'} />
          </div>
          <div><Label>Date Performed</Label><input type="date" value={form.genexpert_date} onChange={e => set('genexpert_date', e.target.value)} style={field()} /></div>
        </div>
      )}

      {/* Step 2 — Culture & DST */}
      {step === 2 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div><Label>Culture Result</Label><Select field="culture_result" options={['Positive', 'Negative', 'Contaminated', 'Not done']} /></div>
            <div><Label>Culture Date</Label><input type="date" value={form.culture_date} onChange={e => set('culture_date', e.target.value)} style={field()} /></div>
            <div><Label>DST Date</Label><input type="date" value={form.dst_date} onChange={e => set('dst_date', e.target.value)} style={field()} /></div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 12 }}>First-Line Drug Susceptibility</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[['Isoniazid (H)', 'dst_isoniazid'], ['Rifampicin (R)', 'dst_rifampicin'], ['Pyrazinamide (Z)', 'dst_pyrazinamide'], ['Ethambutol (E)', 'dst_ethambutol']].map(([label, f]) => (
              <div key={f}>
                <Label>{label}</Label>
                <select value={form[f]} onChange={e => set(f, e.target.value)} style={{ ...field(), borderColor: form[f] === 'Resistant' ? T.red + '80' : T.border, background: form[f] === 'Resistant' ? T.red + '15' : T.elevated }}>
                  <option value="">Select...</option>
                  {RESIST_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 12 }}>Second-Line (if MDR confirmed)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['Moxifloxacin (Mfx)', 'dst_moxifloxacin'], ['Bedaquiline (Bdq)', 'dst_bedaquiline']].map(([label, f]) => (
              <div key={f}>
                <Label>{label}</Label>
                <select value={form[f]} onChange={e => set(f, e.target.value)} style={{ ...field(), borderColor: form[f] === 'Resistant' ? T.red + '80' : T.border, background: form[f] === 'Resistant' ? T.red + '15' : T.elevated }}>
                  <option value="">Not tested</option>
                  {RESIST_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 3 — Review */}
      {step === 3 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              ['Smear', form.smear_result || '—'], ['GeneXpert MTB', form.genexpert_result || '—'],
              ['RIF Resistance (GXpert)', form.genexpert_rif_resist || '—'], ['Culture', form.culture_result || '—'],
              ['Isoniazid', form.dst_isoniazid || '—'], ['Rifampicin', form.dst_rifampicin || '—'],
              ['Pyrazinamide', form.dst_pyrazinamide || '—'], ['Ethambutol', form.dst_ethambutol || '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: T.elevated, borderRadius: 6 }}>
                <span style={{ fontSize: 12, color: T.textMuted }}>{l}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: v === 'Resistant' || v === 'DETECTED' ? T.red : v === '—' ? T.textDim : T.text }}>{v}</span>
              </div>
            ))}
          </div>
          <div>
            <Label>Clinical Notes</Label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Additional observations, clinical context..." style={{ ...field(), resize: 'vertical' }} />
          </div>
        </div>
      )}

      {/* Reject modal */}
      {showReject && (
        <div style={{ marginTop: 16, padding: 16, background: T.red + '15', border: `1px solid ${T.red}40`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 8 }}>Rejection Reason</div>
          <select value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ ...field(), marginBottom: 8 }}>
            <option value="">Select reason...</option>
            <option value="Sample received after 12 days — viability compromised">Sample over 12 days — viability compromised</option>
            <option value="Insufficient volume (<2ml)">Insufficient volume (&lt;2ml)</option>
            <option value="Sample contaminated in transit">Sample contaminated in transit</option>
            <option value="Incorrect sample type for requested test">Incorrect sample type</option>
            <option value="Unlabelled or mislabelled sample">Unlabelled or mislabelled sample</option>
            <option value="Leaking container — biosafety risk">Leaking container — biosafety risk</option>
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn(T.red)} onClick={() => { onReject(sample.id, rejectReason); setShowReject(false); }}>Confirm Rejection</button>
            <button style={btn(T.textMuted, 'outline')} onClick={() => setShowReject(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Footer buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {step > 0 && <button style={btn(T.textMuted, 'outline')} onClick={() => setStep(s => s - 1)}>← Back</button>}
          <button style={btn(T.red, 'outline')} onClick={() => setShowReject(true)}>Reject Sample</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {step < 3
            ? <button style={btn(T.accent)} onClick={() => setStep(s => s + 1)}>Next →</button>
            : <button style={{ ...btn(T.green), fontWeight: 700 }} onClick={handleSubmit}>✓ Save Results</button>}
        </div>
      </div>
    </div>
  );
}

// ── FHIR VIEWER ───────────────────────────────────────────────────────
function FHIRViewer({ sample }) {
  const report = buildFHIRDiagnosticReport(sample, sample.lab_result || {}, sample.patient);
  return (
    <div style={{ ...card(), fontFamily: 'Space Mono', fontSize: 11 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.accent }}>FHIR R4 DiagnosticReport</div>
        <button style={btn(T.teal, 'outline')} onClick={() => {
          const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `FHIR_${sample.sample_code}.json`; a.click();
        }}>⬇ Download</button>
      </div>
      <div style={{ background: T.navy, padding: 16, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>
        <pre style={{ color: T.textMuted, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, lineHeight: 1.7 }}>
          {JSON.stringify(report, null, 2)}
        </pre>
      </div>
      <div style={{ marginTop: 12, padding: '10px 14px', background: T.teal + '15', borderRadius: 8, border: `1px solid ${T.teal}40` }}>
        <div style={{ fontSize: 11, color: T.tealLight, fontFamily: 'DM Sans' }}>
          🔗 <strong>Interoperability:</strong> This FHIR R4 DiagnosticReport can be posted to DHIS2, OpenMRS, OpenHIE-compliant systems, and the WHO GLASS database using the standard FHIR REST API endpoint.
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────
export default function App() {
  const [user,         setUser]         = useState(null);
  const [loginForm,    setLoginForm]    = useState({ username: '', password: '' });
  const [loginError,   setLoginError]   = useState('');
  const [samples,      setSamples]      = useState([...SAMPLE_QUEUE]);
  const [wgsQueue,     setWgsQueue]     = useState([...WGS_QUEUE]);
  const [stats,        setStats]        = useState(getLabStats());
  const [selected,     setSelected]     = useState(null);
  const [activeTab,    setActiveTab]    = useState('queue');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFHIR,     setShowFHIR]     = useState(false);
  const [notification, setNotification] = useState(null);

  const notify = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const refresh = () => {
    setSamples([...SAMPLE_QUEUE]);
    setWgsQueue([...WGS_QUEUE]);
    setStats(getLabStats());
  };

  const handleResult = (sampleId, form) => {
    const result = saveLabResult(sampleId, form);
    refresh();
    setSelected(null);
    notify(`Results saved · ${result.classification.classification}${result.wgsTrigger.triggered ? ' · WGS triggered' : ''}`);
    return result;
  };

  const handleReject = (sampleId, reason) => {
    rejectSample(sampleId, reason);
    refresh();
    setSelected(null);
    notify('Sample rejected — clinic notified', 'warning');
  };

  const handleReceive = (sampleId) => {
    receiveSample(sampleId, user?.id);
    refresh();
    notify('Sample received — processing started');
  };

  // ── LOGIN ──
  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${T.navy} 0%, #0D2137 50%, ${T.navyMid} 100%)` }}>
      <style>{css}</style>
      <div style={{ width: 420, animation: 'slideIn 0.4s ease' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: `linear-gradient(135deg, ${T.blue}, ${T.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 16px', boxShadow: `0 0 40px ${T.accent}40` }}>🔬</div>
          <div style={{ fontFamily: 'Space Mono', fontSize: 26, fontWeight: 700, color: T.accent, letterSpacing: '-1px' }}>TBGenSurv</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>Module 2 · NMRL Laboratory Interface</div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>National Microbiology Reference Laboratory · Zimbabwe</div>
        </div>

        <div style={{ ...card({ padding: 28 }) }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Laboratory Sign In</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Credentials issued by Laboratory Manager only</div>

          {loginError && (
            <div style={{ padding: '10px 14px', background: T.red + '20', border: `1px solid ${T.red}40`, borderRadius: 8, fontSize: 13, color: T.red, marginBottom: 16 }}>
              ⚠ {loginError}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Username</div>
            <input style={field()} placeholder="e.g. lab.tech.nmrl" value={loginForm.username} onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))} onKeyDown={e => e.key === 'Enter' && document.getElementById('pwd').focus()} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Password</div>
            <input id="pwd" type="password" style={field()} placeholder="Enter password" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') {
                const u = LAB_USERS[loginForm.username.trim().toLowerCase()];
                if (!u || u.password !== loginForm.password) { setLoginError('Invalid credentials. Contact your Laboratory Manager.'); return; }
                setUser(u); setLoginError('');
              }}} />
          </div>
          <button style={{ ...btn(T.accent), width: '100%', padding: '12px', fontSize: 14, fontWeight: 700 }} onClick={() => {
            const u = LAB_USERS[loginForm.username.trim().toLowerCase()];
            if (!u || u.password !== loginForm.password) { setLoginError('Invalid credentials. Contact your Laboratory Manager.'); return; }
            setUser(u); setLoginError('');
          }}>SIGN IN →</button>

          <div style={{ marginTop: 16, padding: '10px 14px', background: T.teal + '15', borderRadius: 8, fontSize: 12, color: T.textMuted, border: `1px solid ${T.teal}30` }}>
            ℹ No account? Contact your <span style={{ color: T.tealLight, fontWeight: 600 }}>Laboratory Manager</span> for access credentials.
          </div>

          <details style={{ marginTop: 12 }}>
            <summary style={{ fontSize: 12, color: T.textDim, cursor: 'pointer' }}>Demo credentials ▾</summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(LAB_USERS).map(([un, u]) => (
                <div key={un} onClick={() => setLoginForm({ username: un, password: u.password })}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: T.elevated, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                  <span style={{ color: T.accent, fontFamily: 'Space Mono' }}>{un}</span>
                  <span style={{ color: T.textMuted }}>{u.name}</span>
                </div>
              ))}
              <div style={{ fontSize: 10, color: T.textDim, textAlign: 'center', marginTop: 4 }}>Click any row to auto-fill</div>
            </div>
          </details>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: T.textDim }}>
          🔐 FHIR R4 · HL7 · DHIS2 Compliant · Zimbabwe DPA 2021
        </div>
      </div>
    </div>
  );

  const filtered = samples.filter(s => !filterStatus || s.status === filterStatus);
  const tabCounts = {
    queue: samples.filter(s => s.status !== 'COMPLETE' && s.status !== 'REJECTED').length,
    complete: samples.filter(s => s.status === 'COMPLETE').length,
    wgs: wgsQueue.length,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <style>{css}</style>

      {/* Notification */}
      {notification && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, padding: '12px 20px', borderRadius: 10, background: notification.type === 'warning' ? T.amber + '20' : T.green + '20', border: `1px solid ${notification.type === 'warning' ? T.amber : T.green}50`, color: notification.type === 'warning' ? T.amber : T.green, fontSize: 13, fontWeight: 600, animation: 'slideIn 0.3s ease', maxWidth: 380 }}>
          {notification.type === 'warning' ? '⚠' : '✓'} {notification.msg}
        </div>
      )}

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: T.navyMid, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'Space Mono', fontSize: 16, fontWeight: 700, color: T.accent }}>🔬 TBGenSurv</span>
          <span style={{ fontSize: 11, color: T.textDim, background: T.elevated, padding: '3px 10px', borderRadius: 99 }}>Module 2 · NMRL Lab</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {wgsQueue.filter(w => w.status === 'PENDING').length > 0 && (
            <div style={{ ...badge(T.purple, T.purple + '25'), animation: 'pulse 2s infinite', cursor: 'pointer' }} onClick={() => setActiveTab('wgs')}>
              🧬 {wgsQueue.filter(w => w.status === 'PENDING').length} WGS pending
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: T.elevated, borderRadius: 8 }}>
            <span style={{ fontSize: 14 }}>🔬</span>
            <span style={{ fontSize: 13, color: T.text }}>{user.name}</span>
            <span style={{ ...badge(T.blueLight, T.blueLight + '25'), fontSize: 10 }}>{user.role.replace('_', ' ')}</span>
          </div>
          <button style={btn(T.red, 'outline')} onClick={() => setUser(null)}>Sign Out</button>
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px', maxWidth: 1600, margin: '0 auto', width: '100%' }}>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 12, marginBottom: 24 }}>
          <StatCard icon="📥" value={stats.total_received}  label="Total Received" color={T.blueLight} />
          <StatCard icon="⏳" value={stats.pending_receipt} label="Awaiting" color={T.amber} sub="action needed" onClick={() => setFilterStatus('RECEIVED_NMRL')} />
          <StatCard icon="🔬" value={stats.processing}      label="Processing"    color={T.blue} pulse onClick={() => setFilterStatus('PROCESSING')} />
          <StatCard icon="✅" value={stats.completed_today} label="Complete"       color={T.green} onClick={() => setFilterStatus('COMPLETE')} />
          <StatCard icon="❌" value={stats.rejected}        label="Rejected"       color={T.red} onClick={() => setFilterStatus('REJECTED')} />
          <StatCard icon="🧬" value={stats.wgs_pending}     label="WGS Queue"      color={T.purple} onClick={() => setActiveTab('wgs')} />
          <StatCard icon="⚠" value={stats.mdr_detected}    label="MDR-TB"         color={T.red} sub="detected" />
          <StatCard icon="⏱" value={stats.turnaround_avg + 'd'} label="Avg Turnaround" color={T.teal} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1.2fr 1fr' : '1fr', gap: 20 }}>

          {/* Left — Sample queue */}
          <div>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
              {[
                { id: 'queue',    label: 'Processing Queue', count: tabCounts.queue    },
                { id: 'complete', label: 'Completed',        count: tabCounts.complete },
                { id: 'wgs',      label: '🧬 WGS Queue',    count: tabCounts.wgs      },
                { id: 'interop',  label: '🔗 Interoperability' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '8px 16px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s', background: activeTab === tab.id ? T.cardBg : T.elevated, color: activeTab === tab.id ? T.accent : T.textMuted, borderBottom: activeTab === tab.id ? `2px solid ${T.accent}` : '2px solid transparent' }}>
                  {tab.label} {tab.count != null && <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 99, background: T.elevated, fontSize: 11 }}>{tab.count}</span>}
                </button>
              ))}
            </div>

            {/* Filter chips */}
            {activeTab !== 'interop' && activeTab !== 'wgs' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[['', 'All'], ['RECEIVED_NMRL', 'Awaiting'], ['PROCESSING', 'Processing'], ['COMPLETE', 'Complete'], ['REJECTED', 'Rejected']].map(([v, l]) => (
                  <button key={v} onClick={() => setFilterStatus(v)} style={{ padding: '5px 12px', borderRadius: 99, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: filterStatus === v ? T.accent : T.elevated, color: filterStatus === v ? T.navy : T.textMuted, transition: 'all 0.2s' }}>{l}</button>
                ))}
              </div>
            )}

            {/* Queue table */}
            {(activeTab === 'queue' || activeTab === 'complete') && (
              <div style={{ ...card({ padding: 0, overflow: 'hidden' }) }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px 110px 90px 80px', padding: '10px 20px', background: T.elevated, fontSize: 11, fontWeight: 600, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.7px', borderBottom: `1px solid ${T.border}` }}>
                  <span>Sample</span><span>Received</span><span>Status</span><span>Classification</span><span>Priority</span><span>WGS</span>
                </div>
                {filtered.filter(s => activeTab === 'complete' ? s.status === 'COMPLETE' : s.status !== 'COMPLETE' && s.status !== 'REJECTED').length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: T.textDim }}>No samples in this category</div>
                  : filtered.filter(s => activeTab === 'complete' ? s.status === 'COMPLETE' : s.status !== 'COMPLETE' && s.status !== 'REJECTED')
                    .map(s => <SampleRow key={s.id} sample={s} onSelect={setSelected} selected={selected?.id === s.id} />)
                }
              </div>
            )}

            {/* WGS Queue */}
            {activeTab === 'wgs' && (
              <div style={{ ...card({ padding: 0, overflow: 'hidden' }) }}>
                <div style={{ padding: '14px 20px', background: T.elevated, borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.purple }}>🧬 Whole Genome Sequencing Request Queue</div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Auto-triggered when MDR/RR/XDR-TB is detected · Routes to Module 3 bioinformatics pipeline</div>
                </div>
                {wgsQueue.length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: T.textDim }}>No WGS requests pending</div>
                  : wgsQueue.map(w => (
                    <div key={w.id} style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'grid', gridTemplateColumns: '1fr 100px 100px 120px', gap: 12, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontFamily: 'Space Mono', fontSize: 13, color: T.accent, marginBottom: 2 }}>{w.sample_code}</div>
                        <div style={{ fontSize: 12, color: T.textMuted }}>{w.clinic} · {w.province}</div>
                        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                          {w.reason.map((r, i) => <span key={i} style={{ fontSize: 10, padding: '2px 7px', background: T.red + '20', color: T.red, borderRadius: 99, border: `1px solid ${T.red}30` }}>{r}</span>)}
                        </div>
                      </div>
                      <span style={badge(TB_CLASS[w.tb_classification]?.color || T.red, TB_CLASS[w.tb_classification]?.bg || T.red + '20')}>{w.tb_classification}</span>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(w.requested_date).toLocaleDateString()}</span>
                      <span style={{ ...badge(w.status === 'PENDING' ? T.amber : T.green, w.status === 'PENDING' ? T.amber + '20' : T.green + '20'), animation: w.status === 'PENDING' ? 'pulse 2s infinite' : 'none' }}>
                        {w.status === 'PENDING' ? '⏳ Awaiting Module 3' : '✓ Sent to Pipeline'}
                      </span>
                    </div>
                  ))
                }
              </div>
            )}

            {/* Interoperability panel */}
            {activeTab === 'interop' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { name: 'DHIS2', desc: 'Zimbabwe MoHCC national health data platform', icon: '🇿🇼', color: T.amber, status: 'Ready', fields: ['TB lab results', 'MDR/XDR classification', 'Clinic-level aggregate data'] },
                  { name: 'FHIR R4', desc: 'HL7 FHIR standard DiagnosticReport resources', icon: '🏥', color: T.teal, status: 'Active', fields: ['DiagnosticReport', 'Observation resources', 'LOINC coded results'] },
                  { name: 'WHO GLASS', desc: 'Global Antimicrobial Resistance Surveillance', icon: '🌍', color: T.blueLight, status: 'Ready', fields: ['Drug resistance profiles', 'Lineage data (via WGS)', 'Aggregate country data'] },
                  { name: 'Africa CDC PGI', desc: 'Africa Pathogen Genomics Initiative', icon: '🧬', color: T.purple, status: 'Planned', fields: ['WGS sequence data', 'Phylogenetic metadata', 'Continental surveillance'] },
                  { name: 'OpenMRS', desc: 'Used at mission hospitals across Zimbabwe', icon: '⚕️', color: T.green, status: 'Planned', fields: ['Patient episode linking', 'Treatment outcomes', 'Follow-up results'] },
                  { name: 'HL7 v2 (ASTM)', desc: 'Legacy lab system messaging', icon: '📡', color: T.textMuted, status: 'Planned', fields: ['OBR/OBX messages', 'Lab order integration', 'LIS connectivity'] },
                ].map(sys => (
                  <div key={sys.name} style={{ ...card() }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{sys.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, color: sys.color }}>{sys.name}</div>
                          <div style={{ fontSize: 11, color: T.textMuted }}>{sys.desc}</div>
                        </div>
                      </div>
                      <span style={{ ...badge(sys.status === 'Active' ? T.green : sys.status === 'Ready' ? T.amber : T.textDim, sys.status === 'Active' ? T.green + '20' : sys.status === 'Ready' ? T.amber + '20' : T.textDim + '20'), fontSize: 10 }}>
                        {sys.status === 'Active' ? '● Active' : sys.status === 'Ready' ? '○ Ready' : '◌ Planned'}
                      </span>
                    </div>
                    {sys.fields.map(f => <div key={f} style={{ fontSize: 11, color: T.textMuted, padding: '3px 0', borderBottom: `1px solid ${T.border}` }}>· {f}</div>)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right — Detail panel */}
          {selected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selected.status === 'RECEIVED_NMRL' && (
                  <button style={btn(T.blueLight)} onClick={() => { handleReceive(selected.id); setSelected({ ...selected, status: 'PROCESSING' }); }}>
                    🔬 Start Processing
                  </button>
                )}
                {selected.status === 'PROCESSING' && !selected.lab_result && (
                  <button style={{ ...btn(T.green), fontWeight: 700 }} onClick={() => setActiveTab('enter-results')}>
                    ✓ Enter Results
                  </button>
                )}
                {selected.lab_result && (
                  <button style={btn(T.teal, 'outline')} onClick={() => setShowFHIR(!showFHIR)}>
                    {showFHIR ? 'Hide' : 'View'} FHIR Report
                  </button>
                )}
                <button style={btn(T.textMuted, 'outline')} onClick={() => setSelected(null)}>Close ✕</button>
              </div>

              {/* Sample info card */}
              <div style={card()}>
                <div style={{ fontFamily: 'Space Mono', fontSize: 15, color: T.accent, marginBottom: 8 }}>{selected.sample_code}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    ['Clinic',       selected.clinic.name],
                    ['Province',     selected.clinic.province],
                    ['Sample Type',  selected.sample_type],
                    ['Appearance',   selected.appearance],
                    ['Volume',       selected.volume_ml ? `${selected.volume_ml}ml` : '—'],
                    ['Transit Days', selected.transit_days ? `${selected.transit_days} days` : '—'],
                    ['Patient Age',  `${selected.patient.age}y ${selected.patient.sex}`],
                    ['HIV Status',   selected.patient.hiv_status],
                    ['Received',     selected.received_date ? new Date(selected.received_date).toLocaleString() : '—'],
                    ['Priority',     selected.priority],
                  ].map(([l, v]) => (
                    <div key={l} style={{ padding: '7px 10px', background: T.elevated, borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                    </div>
                  ))}
                </div>
                {selected.rejection_reason && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: T.red + '15', borderRadius: 8, border: `1px solid ${T.red}40`, fontSize: 12, color: T.red }}>
                    ❌ Rejected: {selected.rejection_reason}
                  </div>
                )}
              </div>

              {/* Lab results entry or display */}
              {(selected.status === 'PROCESSING' || selected.status === 'RECEIVED_NMRL') && !selected.lab_result ? (
                <LabResultForm sample={selected} onSubmit={(id, form) => { const r = handleResult(id, form); return r; }} onReject={handleReject} onClose={() => setSelected(null)} />
              ) : selected.lab_result ? (
                <div style={card()}>
                  <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>🔬 Laboratory Results</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                      ['Smear', selected.lab_result.smear_result],
                      ['Smear Grade', selected.lab_result.smear_grade],
                      ['GeneXpert MTB', selected.lab_result.genexpert_result],
                      ['RIF Resistance', selected.lab_result.genexpert_rif_resist],
                      ['Culture', selected.lab_result.culture_result],
                      ['INH', selected.lab_result.dst_isoniazid],
                      ['RIF', selected.lab_result.dst_rifampicin],
                      ['PZA', selected.lab_result.dst_pyrazinamide],
                      ['EMB', selected.lab_result.dst_ethambutol],
                    ].filter(([, v]) => v).map(([l, v]) => {
                      const isR = v === 'Resistant' || v === 'DETECTED';
                      return (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', background: T.elevated, borderRadius: 6 }}>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{l}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: isR ? T.red : T.green }}>{v}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selected.lab_result.tb_classification && (
                      <span style={badge(TB_CLASS[selected.lab_result.tb_classification]?.color || T.green, TB_CLASS[selected.lab_result.tb_classification]?.bg || T.green + '20')}>
                        {TB_CLASS[selected.lab_result.tb_classification]?.icon} {selected.lab_result.tb_classification}
                      </span>
                    )}
                    {selected.lab_result.wgs_requested && <span style={badge(T.purple, T.purple + '25')}>🧬 WGS Requested</span>}
                  </div>
                  {selected.lab_result.notes && <div style={{ marginTop: 10, fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>"{selected.lab_result.notes}"</div>}
                </div>
              ) : null}

              {showFHIR && selected.lab_result && <FHIRViewer sample={selected} />}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 24px', background: T.navyMid, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textDim }}>
        <span>TBGenSurv Module 2 · NMRL Laboratory Interface · University of Zimbabwe BIBE</span>
        <span>FHIR R4 · HL7 · DHIS2 · WHO GLASS · Zimbabwe DPA 2021</span>
      </div>
    </div>
  );
}
